'use client';
import {cn} from '@/lib/utils';
import {StrapiBrand, StrapiPostTag} from '@/types/strapi';
import {ChevronDown} from 'lucide-react';
import Link from 'next/link';
import {target} from '../helpers/utils';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../ui/accordion';

export default function BrandCategoryList(Prop: {
  moduleOption: any;
  moduleData: {
    brandCates?: StrapiPostTag[];
    brands?: StrapiBrand[];
  };
}) {
  const {moduleOption, moduleData} = Prop;

  const titleMap = (moduleData?.brandCates || []).reduce(
    (acc: {[key: string]: string}, curr) => {
      acc[curr.slug] = curr.title;
      return acc;
    },
    {}
  );

  // sort brands
  const sortList = moduleOption.sortList || [
    'watsons',
    'kruidvat',
    'trekpleister',
    'rossmann',
    'superdrug',
    'savers',
    'drogas',
    'ici-paris-xl',
    'the-perfume-shop',
    'parknshop',
    'taste',
    'fusion',
    'great',
    'food-parc',
    'fortress',
    'watsons-wine',
    'parknshop-yonghui',
    'watsons-water',
    'mr-juicy',
    'sunkist'
  ];

  const brands = (moduleData?.brands || [])
    .map((b) => {
      b.url = b.url && b.url != '' ? b.url : '/brand/' + b.slug;
      return b;
    })
    .sort((a, b) => {
      const aIndex = sortList.indexOf(a.slug);
      const bIndex = sortList.indexOf(b.slug);
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      } else {
        if (aIndex !== -1) {
          return -1;
        }
        if (bIndex !== -1) {
          return 1;
        }
      }
      return 0;
    });

  const defaultGroupColors = {
    'brand-health-and-beauty': 'bg-[#E21736]',
    'brand-luxury-perfumeries-and-cosmetics': 'bg-[#139cd3]',
    'brand-food-electronics-and-wine': 'bg-[#ff5b00]',
    'brand-beverages': 'bg-[#9B5DE5]',
    'brand-retail-media': 'bg-[#FF00FF]'
  };
  const bgColorMap = {
    ...(moduleOption?.group?.colors || {}),
    ...defaultGroupColors
  };
  const groupSeq =
    moduleOption?.group?.sequence || Object.keys(defaultGroupColors);
  const brandGroupItems: {
    [key: string]: {
      title: string;
      bgColor: string;
      items: StrapiBrand[];
    };
  } = {
    'brand-health-and-beauty': {
      title: titleMap['brand-health-and-beauty'],
      bgColor: bgColorMap['brand-health-and-beauty'],
      items: []
    },
    'brand-luxury-perfumeries-and-cosmetics': {
      title: titleMap['brand-luxury-perfumeries-and-cosmetics'],
      bgColor: bgColorMap['brand-luxury-perfumeries-and-cosmetics'],
      items: []
    },
    'brand-food-electronics-and-wine': {
      title: titleMap['brand-food-electronics-and-wine'],
      bgColor: bgColorMap['brand-food-electronics-and-wine'],
      items: []
    },
    'brand-beverages': {
      title: titleMap['brand-beverages'],
      bgColor: bgColorMap['brand-beverages'],
      items: []
    },
    'brand-retail-media': {
      title: titleMap['brand-retail-media'],
      bgColor: bgColorMap['brand-retail-media'],
      items: []
    }
  };

  brands.map((brand: StrapiBrand) => {
    const brandCateGroups = brand.categories;
    if (brandCateGroups) {
      for (const brandCateGroup of brandCateGroups) {
        if (!brandGroupItems[brandCateGroup]) {
          brandGroupItems[brandCateGroup] = {
            title: titleMap[brandCateGroup],
            bgColor: bgColorMap[brandCateGroup],
            items: []
          };
        }
        brandGroupItems[brandCateGroup].items.push(brand);
      }
    }
  });

  return (
    <div className="questrial text-[18px] flex flex-col gap-4">
      {groupSeq.map((brandGroupKey: string) => {
        if (
          brandGroupItems[brandGroupKey] &&
          brandGroupItems[brandGroupKey].items.length > 0
        ) {
          return (
            <div
              key={brandGroupKey}
              id={brandGroupKey}
              className={cn('h-full')}
            >
              <Accordion type="single" collapsible defaultValue="expanded">
                <AccordionItem value="expanded">
                  <AccordionTrigger
                    className={cn(
                      'p-4 text-white',
                      brandGroupItems[brandGroupKey].bgColor
                    )}
                  >
                    <h2 className={cn('text-2xl font-[700] text-white')}>
                      {brandGroupItems[brandGroupKey].title}
                    </h2>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 text-white'
                      )}
                    />
                  </AccordionTrigger>
                  <AccordionContent>
                    <div
                      className={cn(
                        'transition-all duration-300 ease-in-out transform origin-top '
                      )}
                    >
                      <div className="flex flex-wrap">
                        {brandGroupItems[brandGroupKey].items.map(
                          (brand, index) => {
                            return (
                              <div
                                key={index}
                                id={'brand-item-' + brand.slug}
                                className="py-4 flex justify-center items-center"
                              >
                                <Link
                                  prefetch={false}
                                  href={brand.url}
                                  target={target(brand.url)}
                                >
                                  {brand.logo && (
                                    <img
                                      src={brand.logo?.url}
                                      alt={brand.title}
                                      width={250}
                                      height={80}
                                      className="max-h-[50px] max-w-[105px] w-auto object-contain"
                                    />
                                  )}
                                </Link>
                              </div>
                            );
                          }
                        )}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          );
        }
      })}
    </div>
  );
}
